/**
 * API Service for Hypixel Stats
 * Handles all communication with the backend proxy server
 */

import axios from 'axios';

// API base URL - use environment variable in production, proxy in development
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json'
  }
});

/**
 * Fetch player data by username
 * @param {string} username - Minecraft username
 * @returns {Promise<Object>} Player data object
 */
export const fetchPlayerData = async (username) => {
  try {
    const response = await api.get(`/player/${encodeURIComponent(username)}`);
    return response.data;
  } catch (error) {
    if (error.response) {
      // Server responded with error
      throw new Error(error.response.data.error || 'Failed to fetch player data');
    } else if (error.request) {
      // Request made but no response
      throw new Error('Server is not responding. Please try again later.');
    } else {
      // Request setup error
      throw new Error('An error occurred. Please try again.');
    }
  }
};

/**
 * Compare two players
 * @param {string} username1 - First player's username
 * @param {string} username2 - Second player's username
 * @returns {Promise<Object>} Comparison data
 */
export const comparePlayers = async (username1, username2) => {
  try {
    const response = await api.get(
      `/compare/${encodeURIComponent(username1)}/${encodeURIComponent(username2)}`
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.error || 'Failed to compare players');
    }
    throw new Error('An error occurred while comparing players.');
  }
};

/**
 * Health check for the API
 * @returns {Promise<boolean>} True if API is healthy
 */
export const checkHealth = async () => {
  try {
    const response = await api.get('/health');
    return response.data.status === 'ok';
  } catch {
    return false;
  }
};

export default api;
